/**
 * Help & Support Screen
 * Email Support + FAQ
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  StatusBar,
  Linking,
  ScrollView,
} from 'react-native';
import Icon from '../components/Icon';
import { useTheme } from '../context/ThemeContext';

const HelpSupportScreen = ({ navigation }) => {
  // All hooks must be called first, in the same order
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const { isDarkMode, colors } = useTheme();

  const faqItems = [
    {
      id: 1,
      question: 'How do I withdraw my earnings?',
      answer: 'Go to your Wallet or Referral section, click "Withdraw", select your preferred method (Bank Transfer, UPI, or USDT), enter the amount, and submit. Processing time depends on the method you choose.'
    },
    {
      id: 2,
      question: 'What are the minimum withdrawal amounts?',
      answer: 'Bank Transfer: $100, UPI: $10, USDT: $50. You must have at least these amounts in your account to initiate a withdrawal.'
    },
    {
      id: 3,
      question: 'How long does withdrawal take?',
      answer: 'Bank Transfer: 3-5 business days, UPI: Instant, USDT: 1-6 hours. The exact time depends on your bank and payment method.'
    },
    {
      id: 4,
      question: 'What is the referral program?',
      answer: 'Invite friends to join using your referral link. You earn commissions from their trading activity. The more people you refer, the more you earn!'
    },
    {
      id: 5,
      question: 'How do I verify my account?',
      answer: 'Go to Profile > Edit Profile > Verify Account. Upload your ID proof and complete the verification process. This usually takes 24-48 hours.'
    },
    {
      id: 6,
      question: 'Is my data secure?',
      answer: 'Yes, we use industry-standard encryption and security protocols to protect your personal and financial information.'
    },
    {
      id: 7,
      question: 'Can I change my password?',
      answer: 'Yes, go to Settings > Security > Change Password. You\'ll need to verify your current password before setting a new one.'
    },
    {
      id: 8,
      question: 'How do I contact support?',
      answer: 'You can reach our support team at Support@onetradebot.com. We typically respond within 24 hours.'
    }
  ];

  const openEmail = () => {
    Linking.openURL('mailto:Support@onetradebot.com');
  };

  const toggleFAQ = (id) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar
        barStyle={isDarkMode ? 'light-content' : 'dark-content'}
        backgroundColor={colors.background}
      />

      {/* Header */}
      <View
        style={[
          styles.header,
          {
            backgroundColor: colors.headerBackground,
            borderBottomColor: colors.border,
          },
        ]}
      >
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Icon name="arrow-back" size={24} color={colors.primary} />
        </TouchableOpacity>

        <Text style={[styles.headerTitle, { color: colors.primary }]}>
          Help & Support
        </Text>

        <View style={{ width: 24 }} />
      </View>

      <ScrollView showsVerticalScrollIndicator={false} style={styles.scrollView}>
        {/* Email Support Card */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Support</Text>
          <TouchableOpacity
            style={[
              styles.emailCard,
              {
                backgroundColor: colors.cardBackground,
                borderColor: colors.border,
              },
            ]}
            onPress={openEmail}
          >
            <View style={styles.iconBox}>
              <Icon name="mail" size={26} color="#4A90E2" />
            </View>

            <View style={{ flex: 1 }}>
              <Text style={[styles.title, { color: colors.text }]}>
                Email Support
              </Text>
              <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
                Support@onetradebot.com
              </Text>
            </View>

            <Icon
              name="chevron-forward"
              size={20}
              color={colors.textTertiary}
            />
          </TouchableOpacity>
        </View>

        {/* FAQ Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Frequently Asked Questions</Text>
          
          {faqItems.map((item) => (
            <View key={item.id}>
              <TouchableOpacity
                style={[
                  styles.faqQuestion,
                  {
                    backgroundColor: colors.cardBackground,
                    borderColor: expandedFAQ === item.id ? colors.primary : colors.border,
                  },
                ]}
                onPress={() => toggleFAQ(item.id)}
              >
                <View style={styles.questionContent}>
                  <Icon 
                    name="help-circle" 
                    size={20} 
                    color={colors.primary}
                    style={{ marginRight: 12 }}
                  />
                  <Text 
                    style={[
                      styles.questionText,
                      { color: colors.text }
                    ]}
                  >
                    {item.question}
                  </Text>
                </View>
                <Icon
                  name={expandedFAQ === item.id ? 'chevron-up' : 'chevron-down'}
                  size={20}
                  color={colors.textSecondary}
                />
              </TouchableOpacity>

              {expandedFAQ === item.id && (
                <View
                  style={[
                    styles.faqAnswer,
                    {
                      backgroundColor: colors.cardBackground,
                      borderBottomColor: colors.border,
                    },
                  ]}
                >
                  <Text style={[styles.answerText, { color: colors.textSecondary }]}>
                    {item.answer}
                  </Text>
                </View>
              )}
            </View>
          ))}
        </View>

        <View style={{ height: 30 }} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 50,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  section: {
    paddingHorizontal: 16,
    marginTop: 20,
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
  },
  content: {
    padding: 16,
    marginTop: 20,
  },
  emailCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  iconBox: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#4A90E220',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
  },
  subtitle: {
    fontSize: 13,
    marginTop: 2,
  },
  faqQuestion: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 0,
  },
  questionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 10,
  },
  questionText: {
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
    lineHeight: 20,
  },
  faqAnswer: {
    padding: 16,
    borderRadius: 0,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    marginBottom: 12,
  },
  answerText: {
    fontSize: 13,
    lineHeight: 20,
  },
});

export default HelpSupportScreen;
